import router from "../../router/index";
import axios from "axios";
import { CustomAlert } from "@/assets/js/util";
import { AUTH_URL } from "@/config";
import { runLogoutHooks } from "@/moduleHooks";
import { activeModules } from "@/router/module-loader";
import { ensureChatConnected, chatDisConnect } from "@/services/socket";
import { takeRedirect } from "@/utils/authRedirect";
import i18n from "@/i18n";

// ───── 비밀번호 RSA 암호화 (서버 공개키 사용) ─────────────
function pemToArrayBuffer(pem) {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, "")
    .replace(/-----END PUBLIC KEY-----/, "")
    .replace(/\s+/g, "");
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export async function encryptPassword(password) {
  const { data } = await axios.get(`${AUTH_URL}/public-key`);
  const keyBuffer = pemToArrayBuffer(data.public_key);
  const publicKey = await crypto.subtle.importKey(
    "spki",
    keyBuffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"],
  );
  const encoded = new TextEncoder().encode(password);
  const encrypted = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, encoded);
  return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
}

const loginStore = {
  state: {
    userInfo: null,
    isLogin: false,
    testModule: "MOD_REMOTE",
    grantedApps: [], // 계정에 부여된 앱(모듈) 목록: ["HISTORY_WEB", "REMOTE_APP"]
  },

  getters: {
    isLogin(state) {
      return state.isLogin;
    },
    selectedModule(state) {
      return state.testModule;
    },
    isAdmin(state) {
      return state.userInfo?.IsAdmin || false;
    },
    isSystem(state) {
      return state.userInfo?.IsSystem || false;
    },
    grantedApps(state) {
      return state.grantedApps || [];
    },
    // 로그인 사용자 소속코드 (Check 정적파일 회사별 경로 구성 등에 사용)
    companyCode(state) {
      return state.userInfo?.companyCode || "";
    },
  },

  mutations: {
    loginSuccess: function (state, payload) {
      state.userInfo = payload;
      state.isLogin = true;
      // 로그인 전 요청했던 위치로 복귀.
      // 1순위: sessionStorage(401/세션만료 흐름 — URL 노출 없이 저장됨, takeRedirect 가 내부경로 검증)
      // 2순위: returnUrl 쿼리(외부 공유 링크 등 레거시 경로 호환). 내부 경로만 허용 — open-redirect 방지.
      const rt = router.currentRoute.value?.query?.returnUrl;
      const queryDest =
        typeof rt === "string" && rt.startsWith("/") && !rt.startsWith("//")
          ? rt
          : null;
      const dest = takeRedirect() || queryDest || "/";
      router.replace(dest);
    },
    setGrantedApps: function (state, apps) {
      state.grantedApps = apps || [];
    },
    logoutSuccess: function (state) {
      state.userInfo = null;
      state.isLogin = false;
      state.grantedApps = [];
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("vuex");
      sessionStorage.removeItem("remoteGuestAccess"); // 게스트 Zone 임시 접근 플래그 정리
      router.replace({ path: "/login" });
    },
    updateSelectedModule: function (state, newValue) {
      state.testModule = newValue;
    },
    // 비밀번호 만료 플래그 해제 (변경 완료 시) — 라우터 가드 해제용
    setPasswordExpired: function (state, val) {
      if (state.userInfo) state.userInfo.PasswordExpired = val;
    },
  },

  actions: {
    async login(dispatch, loginObj) {
      try {
        // 로그인 전 이전 세션 흔적 정리(로그아웃 처리 선행).
        // lastLoginSession='LOGIN' 인데 토큰이 없는 좀비 상태 등에서, 깨끗한 상태로 로그인 시작.
        // 서버는 단일세션(새 로그인=이전 세션 REVOKE)이라 클라이언트 잔여물만 비우면 됨.
        // (needConfirm→force 재진입 시엔 이미 정리됐으므로 스킵)
        if (loginObj.__force !== true) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("vuex");
          sessionStorage.removeItem("remoteGuestAccess");
          delete axios.defaults.headers.common.Authorization;
          localStorage.setItem("lastLoginSession", "LOGOUT");
        }

        // 비밀번호를 서버 공개키로 RSA 암호화 후 전송 (Auth 서버가 복호화)
        const encryptedPassword = await encryptPassword(loginObj.UserPassword);
        // 기존: 8731 로그인 래퍼 경유 (내부에서 8723 SSO 전달 + 로컬 미러 ensureLocalUserByUserId)
        // const res = await axios.post("/api/users/login", {
        //   ...loginObj,
        //   UserPassword: encryptedPassword,
        // });
        // 변경: Auth 서버(auth-dev) 직결 로그인 (POST /api/auth/login)
        const res = await axios.post(`${AUTH_URL}/login`, {
          user_id: loginObj.UserId,
          password: encryptedPassword,
          force: loginObj.__force === true, // 확인 팝업에서 "강제종료 후 로그인" 선택 시 true
        });

        // 기존 사용자가 "원격중"이면 강제종료 불가 → 로그인 차단 알림만(force 재요청 없음)
        if (res.data && res.data.remoteBusy) {
          CustomAlert(i18n.global.t("login.remoteBusy"));
          return;
        }

        // 동시 로그인 감지 — 기존 활성 세션(launch 제외)이 있으면 확인 팝업 → 확인 시 force 재요청
        if (res.data && res.data.needConfirm) {
          CustomAlert(
            i18n.global.t("login.duplicateConfirm"),
            true,
            () => {
              this.dispatch("login", { ...loginObj, __force: true });
            },
            null,
          );
          return;
        }

        const accessToken = res.data.access_token;
        const refreshToken = res.data.refresh_token;
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        // 마지막 세션 상태 = LOGIN(정상 로그아웃 시 LOGOUT 으로 바뀜). 비정상 종료면 LOGIN 으로 남아
        // 다음 로그인 화면 진입 시 정리(marker).
        localStorage.setItem("lastLoginSession", "LOGIN");
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        const userId = loginObj.UserId;

        // Auth 로그인 응답의 user 객체를 신원 소스로 전달 (계정이 8723 SoT, 8731 미러엔 없을 수 있음)
        this.dispatch("getMemberInfo", { userId, authUser: res.data.user });
      } catch (err) {
        console.log(err);
        const data = err.response?.data;
        if (err.response?.status == 404) {
          CustomAlert("네트워크 에러입니다. 관리자에게 문의하세요.");
        } else if (data && data.code === "ACCOUNT_LOCKED") {
          // 시간 기반 잠금 → N분 후 재시도 안내 (서버가 남은 분을 내려줌)
          CustomAlert(i18n.global.t("login.lockedRetry", { minutes: data.retryAfterMinutes }));
        } else {
          // NestJS 에러는 { statusCode, message, error } 객체 → message 우선 추출 (객체 그대로면 [object Object])
          const msg =
            (data && typeof data === "object" ? data.message : data) ||
            "로그인 중 오류가 발생했습니다.";
          CustomAlert(msg);
        }
      }
    },

    // 다른 시스템에서 받은 launch token으로 자동 로그인
    async exchangeTokenLogin({ dispatch }, { launchToken, appCode }) {
      try {
        const res = await axios.post("/api/users/exchangeToken", {
          launch_token: launchToken,
          appCode: appCode,
        });
        const accessToken = res.data.access_token;
        const refreshToken = res.data.refresh_token;
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("lastLoginSession", "LOGIN");
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

        // 외부 시스템에서 launch로 진입한 세션 표시 (탭 단위)
        // 원격 종료 시 이 탭을 자동으로 닫기 위한 플래그
        sessionStorage.setItem("launchedExternally", "true");

        await dispatch("getMemberInfo", { userId: res.data.UserId });
        return true;
      } catch (err) {
        console.log(err);
        CustomAlert(
          err.response?.data || "자동 로그인 중 오류가 발생했습니다.",
        );
        return false;
      }
    },

    // 계정에 부여된 앱(모듈) 목록 로드 — SideNav/라우터 권한 필터의 기준
    async loadGrantedApps({ commit }) {
      // 1) 개발 테스트 오버라이드
      //    devtools 콘솔에서: localStorage.grantedAppsOverride = "HISTORY_WEB"
      //    (지우려면: localStorage.removeItem("grantedAppsOverride"))
      const override = localStorage.getItem("grantedAppsOverride");
      if (override) {
        commit(
          "setGrantedApps",
          override.split(",").map((s) => s.trim()).filter(Boolean),
        );
        return;
      }

      // 2) Auth 서버에서 내 접근 가능 앱 조회 (7단계: /api/core/nav 로 이관 가능)
      try {
        const token = localStorage.getItem("accessToken");
        const { data } = await axios.get(`${AUTH_URL}/applications/my`, {
          headers: { Authorization: "Bearer " + token },
        });
        const apps = (data || []).map((a) => a.app_code);
        commit("setGrantedApps", apps);
      } catch {
        // 3) 실패 시 폴백: 이 배포에 탑재된 모든 모듈 (dev 편의)
        commit(
          "setGrantedApps",
          activeModules.map((m) => m.appCode).filter(Boolean),
        );
      }
    },

    // 토큰으로 유저 정보를 받아오는 코드
    // authUser = Auth(8723) 로그인 응답의 user 객체 { user_id, name, role, ... }
    async getMemberInfo({ commit, dispatch }, { userId, authUser }) {
      const token = localStorage.getItem("accessToken");

      try {
        // 신원(identity)은 Auth 로그인 응답을 1순위로 사용 (계정 원본=8723).
        // 8731 미러에 없는 Auth 신규계정도 정상 로그인되도록.
        let identity = null;
        if (authUser) {
          identity = {
            UserId: authUser.user_id || userId,
            UserName: authUser.name || userId,
          };
        } else {
          // 폴백: 8731 profile (authUser 없는 경로 — 부팅 복원 등)
          try {
            const profileRes = await axios.get("/api/users/profile", {
              params: { UserId: userId },
              headers: { Authorization: "Bearer " + token },
            });
            const profile = profileRes.data;
            if (profile && profile.UserId) {
              identity = { UserId: profile.UserId, UserName: profile.UserName };
            }
          } catch {
            // 무시 (아래에서 최종 검증)
          }
        }

        if (!identity) {
          CustomAlert("사용자 정보를 가져오는데 실패했습니다.");
          return;
        }

        // 최근 접속시간: Auth 로그인 응답(직전 접속시간)을 1순위로 사용.
        // authUser 없는 경로(launch 등)만 8731 로컬 이력을 best-effort 폴백.
        let lastAccessTime = authUser?.last_access_time || null;
        if (!lastAccessTime) {
          try {
            const lastAccessRes = await axios.get("/api/users/lastAccessTime", {
              params: { UserId: identity.UserId },
            });
            lastAccessTime = lastAccessRes.data?.LastAccessTime || null;
          } catch {
            // 무시
          }
        }

        const userInfo = {
          UserId: identity.UserId,
          UserName: identity.UserName,
          companyCode: authUser?.companyCode ?? identity.Company ?? "", // 소속 코드 (회원정보 소속 매핑용)
          // 관리자 여부 = Auth 계정 role 기반 (ADMIN / SYSTEM). authUser 없는 폴백 경로는 false.
          IsAdmin: authUser?.role === "ADMIN" || authUser?.role === "SYSTEM",
          // SYSTEM 전용 여부 (로그관리 등 SYSTEM 한정 메뉴 게이트용)
          IsSystem: authUser?.role === "SYSTEM",
          LastAccessTime: lastAccessTime,
          HasPin: authUser?.has_pin || false, // Glass PIN 등록 여부 (계정 페이지 라벨용)
          // 비밀번호 만료 정책 (변경주기)
          PasswordExpired: authUser?.password_expired || false,
          PasswordExpiresInDays: authUser?.password_expires_in_days ?? null,
        };
        await dispatch("loadGrantedApps");
        commit("loginSuccess", userInfo);

        // 앱 전역 실시간 소켓 연결 (로그인 세션 내내 유지, 로그아웃 시 해제)
        ensureChatConnected(userInfo.UserId);

        // 신규 로그인 시 비밀번호 만료/임박 안내 (만료면 라우터 가드가 /account 로 강제)
        if (authUser) {
          if (userInfo.PasswordExpired) {
            CustomAlert(i18n.global.t("login.pwExpired"));
          } else if (authUser.password_warn && userInfo.PasswordExpiresInDays != null) {
            // 만료 임박(서버가 경고일 정책으로 판정)
            CustomAlert(
              i18n.global.t("login.pwExpiringSoon", { days: userInfo.PasswordExpiresInDays }),
            );
          }
        }
      } catch (err) {
        CustomAlert(
          err.response?.data || "로그인 정보를 가져오는데 실패했습니다.",
        );
      }
    },
    async logout({ commit }) {
      // 정상 로그아웃 표시 → 다음 로그인 화면에서 "마지막 세션 정리" 스킵(비정상 종료와 구분)
      localStorage.setItem("lastLoginSession", "LOGOUT");
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        try {
          // Auth(8723) 로그아웃 — 세션(refresh token)을 발급한 Auth 서버에서 무효화
          await axios.post(`${AUTH_URL}/logout`, {
            refresh_token: refreshToken,
          });
        } catch {
          // SSO 로그아웃 실패해도 로컬 세션 정리는 계속
        }
      }

      const vuex = localStorage.getItem("vuex");
      if (vuex) {
        const userId = JSON.parse(vuex)?.loginStore?.userInfo?.UserId;
        if (userId) {
          axios.post("/api/users/userHistory", {
            UserId: userId,
            Device: "Web",
            ActionCode: 2,
            MenuCode: "LOGOUT",
          });
        }
      }

      // 앱 전역 실시간 소켓 해제
      chatDisConnect();

      // 모듈 로그아웃 정리 훅 실행 (모듈별 추가 정리용)
      runLogoutHooks();

      commit("logoutSuccess");
    },
  },
};

export default loginStore;
